/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Bouton d'ouverture du module de prise de rendez-vous Calendly.
 *
 * Le script Calendly est charge par `public/index.html`. S'il n'a pas pu se
 * charger — reseau coupe, bloqueur de publicites — `window.Calendly` est absent
 * et le clic retombe sur une navigation classique vers l'URL. Le visiteur garde
 * ainsi un moyen de reserver, au lieu d'un bouton qui ne fait rien.
 */
import React from "react";
import { useLang } from "../i18n/LanguageContext";
import { CalendarClock } from "lucide-react";

const CALENDLY_URL =
  process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/prenim-demo/30min";

const VARIANTS = {
  primary:
    "inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold px-6 py-3 transition-colors shadow-lg shadow-amber-400/20",
  light:
    "inline-flex items-center gap-2 rounded-full bg-white text-blue-700 hover:bg-amber-300 hover:text-slate-950 font-semibold px-7 py-3.5 transition-colors",
  outline:
    "inline-flex items-center gap-2 rounded-full border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-950 font-semibold px-6 py-3 transition-colors",
};

const CalendlyButton = ({
  variant = "primary",
  className = "",
  testId = "calendly-button",
  iconClassName = "h-4 w-4",
  children,
}) => {
  const { t } = useLang();
  const label =
    children ||
    t?.common?.book_consultation ||
    "Book a free 30-min consultation";

  const handleClick = (e) => {
    e.preventDefault();
    if (typeof window !== "undefined" && window.Calendly?.initPopupWidget) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      // Fallback: open in new tab if widget script hasn't loaded yet
      window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={handleClick}
      className={`${VARIANTS[variant] || VARIANTS.primary} ${className}`.trim()}
    >
      <CalendarClock className={iconClassName} />
      <span>{label}</span>
    </button>
  );
};

export default CalendlyButton;
